import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Handle, Position } from "reactflow";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  Box,
  Drawer,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { ArrowDropDown, ArrowForward, Block } from "@mui/icons-material";
import { Edit, ErrorOutline, Person } from "@mui/icons-material";
import ManpowerReqDialog from "components/ManpowerReqDialog";
import { useTranslation } from "react-i18next";
import AnimatedNumber from "components/AnimatedNumber";
import StaffingAndWorkload from "./StaffingAndWorkload";
import { getManpowerEmployeesByJobId } from "services/api";
import EmployeeDrawer from "components/EmployeeManpowerDrawer";
import { getScenarioEmployeesByJobId } from "services/api";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmployeeListDialog from "components/EmployeeListDialog";

const CustomJobNode = ({ data }) => {
  const { t } = useTranslation();
  const [openJobDialog, setOpenJobDialog] = useState(false); // job-level
  const [openEmployeeDrawer, setOpenEmployeeDrawer] = useState(false); // employee-level
  const [employeeLevel, setEmployeeLevel] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  const handleOpenEmployees = (e) => {
    e.stopPropagation();
    if (data.isScenario) {
      handleEmployeeDrawerOpen(e);
    } else {
      setShowEmployeeModal(true);
    }
  };

  const handleJobDialogOpen = (e) => {
    e.stopPropagation();
    setOpenJobDialog(true);
  };

  const handleJobDialogClose = () => setOpenJobDialog(false);

  const handleEmployeeDrawerOpen = async (e) => {
    setOpenEmployeeDrawer(true);

    e.stopPropagation();
    setDrawerLoading(true);

    if (data.loc === "/manpower/simulation/scenarios") {
      getScenarioEmployeesByJobId(data?.job?.id).then((data) => {
        if (data != "error") setEmployeeLevel(data);
        setDrawerLoading(false);
      });
    } else {
      getManpowerEmployeesByJobId(data?.job?.jobId).then((data) => {
        if (data != "error") setEmployeeLevel(data);
        setDrawerLoading(false);
      });
    }
  };

  const handleEmployeeDrawerClose = () => {
    setEmployeeLevel([]);
    setOpenEmployeeDrawer(false);
  };

  const saveJobLevelData = (newData) => {
    data.handleSave(newData);
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {/* 1. Top Handle - Functionality of CustomJobNode, Style of SignoffNode (Invisible) */}
        <Handle type="target" position={Position.Top} style={{ opacity: 0, border: "none" }} />

        {/* 2. Floating Salary - Preserved exactly from CustomJobNode */}
        {data.salary !== null &&
          data.fte !== null &&
          typeof data.salary === "number" &&
          typeof data.fte === "number" &&
          !isNaN(data.salary) &&
          !isNaN(data.fte) && (
            <motion.div
              style={{
                position: "absolute",
                top: -23,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10, // Ensure it floats above
                backgroundColor: "transparent",
                padding: "2px 6px",
                borderRadius: "4px",
                fontWeight: "bold",
                fontSize: "13px",
                color: "red",
              }}
            >
              {"-"}
              <AnimatedNumber value={Number(data.salary) * Number(data.fte)} prefix="$" />
            </motion.div>
          )}

        {/* 3. Main Container - Styled like SignoffNode (Paper, BorderLeft, etc.) */}
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            minWidth: 180,
            textAlign: "left", // Signoff style
            backgroundColor: "#ffffff",
            borderLeft: "4px solid",
            borderLeftColor: "primary.main", // Signoff accent
            borderTop: "1px solid #e0e0e0",
            borderRight: "1px solid #e0e0e0",
            borderBottom: "1px solid #e0e0e0",
            borderRadius: "4px",
            position: "relative", // Needed for absolute positioning of action buttons
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
              transform: "translateY(-2px)",
            },
          }}
        >
          {/* 4. Action Buttons (Edit/View) - Preserved Logic, Positioned Absolute Top-Right */}
          {data.manpowerReqDataRecieved && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                flexDirection: "row", // Changed to row to fit better in the header area
                gap: "4px",
                zIndex: 5,
              }}
            >
              {/* Job-Level Dialog Trigger */}
              <Tooltip
                title={
                  !data.manpowerReq
                    ? t("admin.manpowerRequirement.addJobManpower")
                    : t("admin.manpowerRequirement.editManpower")
                }
                arrow
              >
                <IconButton
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #eee",
                    color: data.manpowerReq ? "blue" : "red",
                    "&:hover": {
                      backgroundColor: data.manpowerReq ? "#e5f0ff" : "#ffe5e5",
                    },
                    width: 24,
                    height: 24,
                  }}
                  disabled={data?.disabled ?? false}
                  onClick={handleJobDialogOpen}
                >
                  {data.manpowerReq ? (
                    <Edit sx={{ fontSize: 14 }} />
                  ) : (
                    <ErrorOutline sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>

              {/* Employee-Level Drawer Trigger */}
              <Tooltip title={t("admin.manpowerRequirement.viewEmployeeRequirements")} arrow>
                <IconButton
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #eee",
                    color: "primary.main",
                    "&:hover": { backgroundColor: "#e5ffe5" },
                    width: 24,
                    height: 24,
                  }}
                  onClick={handleEmployeeDrawerOpen}
                  disabled={data?.disabled ?? false}
                >
                  <Person sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box p={2}>
            {/* 5. Job Label - Typography style from SignoffNode */}
            <Tooltip title={data.label} arrow>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "#344767",
                  mb: 0.5,
                  pr: 6, // Add padding right so text doesn't overlap action buttons
                }}
              >
                {data.label}
              </Typography>
            </Tooltip>

            {/* 6. Job Group - Logic preserved, styled to match Signoff Subtitle */}
            {data.jobGroup && (
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  color: "#7b809a",
                  fontSize: "11px", // Slightly smaller to fit logic
                  fontWeight: 500,
                  display: "block",
                  mb: 1,
                }}
              >
                {data.jobGroup}
              </Typography>
            )}

            {/* 7. Custom Data Component - Preserved */}
            <Box sx={{ mb: 1 }}>
              <StaffingAndWorkload data={data} t={t} />
            </Box>

            {/* Divider from Signoff Style */}
            <Divider sx={{ my: 1, opacity: 0.6 }} />

            {/* 8. Footer Section (FTE & Toggle) */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {/* FTE Display - Signoff Style */}
              {/* === CLICKABLE FTE SECTION === */}
              <Tooltip title={data.refreshFtes ? t("admin.jobs.viewEmp") : ""} placement="top">
                <MDBox
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap={0.5}
                  onClick={handleOpenEmployees}
                  sx={{
                    cursor: "pointer",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.05)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <MDTypography variant="caption" color="text" sx={{ lineHeight: 1, opacity: 0.8 }}>
                    {t("admin.jobs.fte")} -
                  </MDTypography>
                  <MDTypography variant="caption" fontWeight="bold">
                    {(() => {
                      const val = data.fte;
                      if (val === null || val === undefined) {
                        return data.manpowerReqDataRecieved ? "0" : "-";
                      }
                      return val % 1 === 0 ? val : val.toFixed(1);
                    })()}
                  </MDTypography>
                </MDBox>
              </Tooltip>
              {/* Expand/Collapse Toggle - Preserved Functionality */}
              {data.onToggle && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (data.onToggle) data.onToggle(e, data.id, data.isExpanded);
                  }}
                  disabled={data.isLeaf}
                  // title={data.isLeaf ? "No subordinates" : data.isExpanded ? "Collapse" : "Expand"}
                  title={
                    data.isLeaf
                      ? t("admin.jobs.noSubordinates")
                      : data.isExpanded
                      ? t("admin.jobs.collapseNode")
                      : t("admin.jobs.expandNode")
                  }
                  sx={{
                    padding: 0,
                    width: 24,
                    height: 24,
                    opacity: data.isLeaf ? 0.3 : 1,
                    color: "#344767",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                  }}
                >
                  {data.isLeaf ? (
                    <Block sx={{ fontSize: 16, color: "grey.500" }} />
                  ) : data.isExpanded ? (
                    <ArrowDropDown sx={{ fontSize: 20 }} />
                  ) : (
                    <ArrowForward sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              )}
            </Box>
          </Box>
        </Paper>

        {/* 9. Bottom Handle - Functionality of CustomJobNode, Style of SignoffNode (Invisible) */}
        <Handle type="source" position={Position.Bottom} style={{ opacity: 0, border: "none" }} />
      </Box>

      {/* JOB-LEVEL dialog (existing) */}
      <ManpowerReqDialog
        open={openJobDialog}
        onClose={handleJobDialogClose}
        manpowerReq={data.manpowerReq}
        onSave={saveJobLevelData}
        jobTitle={data.label}
        job={data.job}
        allJobs={data.allJobs}
        isScenario={data.isScenario}
        loc={data.loc}
        scenario={data.scenario}
      />

      {/* EMPLOYEE-LEVEL Drawer (new) */}
      <EmployeeDrawer
        open={openEmployeeDrawer}
        onClose={handleEmployeeDrawerClose}
        employeeLevel={employeeLevel}
        drawerLoading={drawerLoading}
        setEmployeeLevel={setEmployeeLevel}
        isScenario={data.loc === "/manpower/simulation/scenarios"}
        jobId={data.loc === "/manpower/simulation/scenarios" ? data?.job?.id : data?.job?.jobId}
      />

      {!data.isScenario && data.refreshFtes && (
        <EmployeeListDialog
          open={showEmployeeModal}
          onClose={() => setShowEmployeeModal(false)}
          job={data.job} // Or whatever holds the Job Name
          isScenario={data.isScenario}
          refreshFtes={data.refreshFtes}
        />
      )}
    </>
  );
};

CustomJobNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    jobGroup: PropTypes.string,
    fte: PropTypes.number,
    manpowerReq: PropTypes.object,
    manpowerReqDataRecieved: PropTypes.bool,
    handleSave: PropTypes.func,
    // fetchRevenueGenerators: PropTypes.func,
    totalEmployees: PropTypes.number,
    salary: PropTypes.number,
    reportingToSolid: PropTypes.bool,
    job: PropTypes.object, // 👈 single job
    allJobs: PropTypes.array, // 👈 all jobs
    isScenario: PropTypes.bool,
    disabled: PropTypes.bool,
    loc: PropTypes.string,
    scenario: PropTypes.object,
    addNumberOfEmployees: PropTypes.func,
    handleDeleteEmployee: PropTypes.func,
    onToggle: PropTypes.func,
    isExpanded: PropTypes.bool,
    isLeaf: PropTypes.bool,
    id: PropTypes.number,
    refreshFtes: PropTypes.func,
  }).isRequired,
};

export default CustomJobNode;
